import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate, useParams } from "react-router-dom";
import { pushApi } from "../../api/push";
import {
  getPanelPushSubscription,
  subscribePanelPush,
  unsubscribePanelPush,
  webPushSupported,
} from "../../utils/webPushClient";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>{title}</Typography>
            <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 950 }}>{value}</Typography>
            {helper ? <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)" }}>{helper}</Typography> : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectNotificationsPage({ embedded = false }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [browserSubscribed, setBrowserSubscribed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [form, setForm] = React.useState({
    audience: "storefront",
    title: "",
    body: "",
    url: "/",
  });

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await pushApi.status(projectId);
      setData(response);
      if (webPushSupported()) {
        const subscription = await getPanelPushSubscription();
        setBrowserSubscribed(Boolean(subscription));
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar Web Push");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const activate = async () => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const subscription = await subscribePanelPush(data?.publicKey);
      await pushApi.subscribe(projectId, subscription.toJSON(), {
        billing: true,
        orders: true,
        payments: true,
        campaigns: false,
      });
      setBrowserSubscribed(true);
      setMessage("Este navegador ya recibe alertas del proyecto.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo activar las notificaciones");
    } finally {
      setWorking(false);
    }
  };

  const deactivate = async () => {
    try {
      setWorking(true);
      setError("");
      const result = await unsubscribePanelPush();
      if (result.endpoint) await pushApi.unsubscribe(projectId, result.endpoint);
      setBrowserSubscribed(false);
      setMessage("Las alertas se desactivaron en este navegador.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo desactivar las notificaciones");
    } finally {
      setWorking(false);
    }
  };

  const send = async (event) => {
    event.preventDefault();
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const response = await pushApi.send(projectId, {
        ...form,
        category: form.audience === "storefront" ? "campaigns" : "system",
      });
      const result = response.result || {};
      setMessage(`Notificación procesada: ${result.sent || 0} enviadas, ${result.failed || 0} fallidas.`);
      setForm((current) => ({ ...current, title: "", body: "" }));
      await load();
    } catch (err) {
      setError(err.message || "No se pudo enviar la notificación");
    } finally {
      setWorking(false);
    }
  };

  const canSend = ["owner", "admin", "superadmin"].includes(data?.role);

  if (loading && !data) {
    return <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant={embedded ? "h5" : "h3"} sx={{ fontWeight: 950, color: BRAND.dark }}>Notificaciones Web Push</Typography>
          <Typography sx={{ mt: 0.5, color: "rgba(25,36,47,.64)" }}>
            Recibí alertas de billing, pedidos y pagos; también podés enviar novedades a quienes se suscriban desde la tienda.
          </Typography>
        </Box>
        <Chip
          icon={browserSubscribed ? <NotificationsActiveRoundedIcon /> : <NotificationsOffRoundedIcon />}
          label={browserSubscribed ? "Este navegador está suscripto" : "Alertas desactivadas en este navegador"}
          color={browserSubscribed ? "success" : "default"}
          sx={{ fontWeight: 900, alignSelf: "flex-start" }}
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      {!data?.enabled && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              startIcon={<WorkspacePremiumRoundedIcon />}
              onClick={() => navigate(`/dashboard/proyectos/${projectId}/configuracion/plan?feature=Notificaciones%20Web%20Push`)}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Mejorar plan
            </Button>
          }
        >
          El módulo Web Push no está activo en este proyecto.
        </Alert>
      )}

      {data?.enabled && !data?.configured && (
        <Alert severity="warning">El módulo está activo, pero todavía faltan las claves VAPID en el backend.</Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Metric icon={<NotificationsActiveRoundedIcon />} title="Equipo conectado" value={data?.ownerSubscribers || 0} helper="Navegadores del panel" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<PeopleAltRoundedIcon />} title="Suscriptores de la tienda" value={data?.storefrontSubscribers || 0} helper="Visitantes que aceptaron novedades" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<CampaignRoundedIcon />} title="Envíos recientes" value={data?.recentCampaigns?.length || 0} helper="Últimos registros guardados" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Alertas de este navegador</Typography>
        <Typography sx={{ mt: 0.5, color: "rgba(25,36,47,.62)" }}>
          Cuando las actives vas a recibir renovaciones de suscripción, pagos rechazados, pausas o reactivaciones, nuevos pedidos y pagos aprobados.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          {!browserSubscribed ? (
            <Button disabled={working || !data?.enabled || !data?.configured || !webPushSupported()} onClick={activate} startIcon={<NotificationsActiveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
              Activar notificaciones
            </Button>
          ) : (
            <Button disabled={working} onClick={deactivate} startIcon={<NotificationsOffRoundedIcon />} color="warning" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
              Desactivar en este navegador
            </Button>
          )}
        </Stack>
      </Paper>

      {canSend && (
        <Paper component="form" onSubmit={send} elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Enviar una notificación</Typography>
          <Typography sx={{ mt: 0.5, mb: 2, color: "rgba(25,36,47,.62)" }}>
            Para promociones elegí visitantes. Para una prueba interna elegí equipo del panel.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Audiencia" value={form.audience} onChange={(event) => setForm((current) => ({ ...current, audience: event.target.value }))}>
                <MenuItem value="storefront">Suscriptores de la tienda</MenuItem>
                <MenuItem value="owner">Equipo del panel</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Título" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 160 }} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Mensaje" value={form.body} onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))} inputProps={{ maxLength: 1000 }} required />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="URL al abrir" value={form.url} onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))} helperText="Ejemplo: /#/productos o /dashboard/proyectos/.../pedidos" />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
              <Button type="submit" fullWidth disabled={working || !data?.enabled || !data?.configured} startIcon={<SendRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                Enviar ahora
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Historial reciente</Typography>
        <Stack spacing={1.25} sx={{ mt: 2 }}>
          {(data?.recentCampaigns || []).map((campaign) => (
            <Box key={campaign.id || campaign._id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{campaign.title}</Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>{campaign.body}</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={campaign.audience} />
                  <Chip size="small" color="success" label={`${campaign.sent || 0} enviados`} />
                  {campaign.failed ? <Chip size="small" color="warning" label={`${campaign.failed} fallidos`} /> : null}
                </Stack>
              </Stack>
              <Typography variant="caption" sx={{ mt: 1, display: "block", color: "rgba(25,36,47,.5)" }}>{dateLabel(campaign.createdAt)}</Typography>
            </Box>
          ))}
          {!data?.recentCampaigns?.length && <Alert severity="info">Todavía no hay envíos registrados.</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );
}
